import { useParams } from 'react-router';
import { Gem, Layers, Lock, Percent } from 'lucide-react';
import { DataLoader } from '@/components/ui/DataLoader';
import { PageBreadcrumb } from '@/components/ui/PageBreadcrumb';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CarryPortfolioCompanies } from '@/components/funds-carry/funds/CarryPortfolioTab';
import { portfolioStats } from '@/components/funds-carry/funds/carryPortfolioStats';
import { CarryStatStrip } from '@/components/funds-carry/funds/CarryStatStrip';
import { CarryMemberAgreementButton } from '@/components/funds-carry/funds/CarryMemberAgreementButton';
import {
  carryFundFacts,
  formatShare,
  formatUnits,
  poolShare,
} from '@/components/funds-carry/funds/carryFundValues';
import {
  PAID_IN_STAT,
  vestingStats,
} from '@/components/funds-carry/funds/carryUnitStats';
import type { CarryMemberPortfolio } from '@/types/carryFund';
import { formatCurrencyCompact } from '@/utils/formatters';
import { InformationAsOfLabel } from '@/portal/shared/components';
import { CarryFundDocumentsList } from '../components/CarryFundDocumentsList';
import { CarryVestingProgress } from '../components/CarryVestingProgress';
import {
  useCarryFundDocuments,
  useCarryHolding,
  useCarryHoldingPortfolio,
  useDownloadCarryDocument,
} from '../hooks/useCarry';
import type {
  CarryFundDocuments,
  CarryHoldingDetail,
} from '../services/carryService';
import { PORTAL_CARRY_PATH } from '../carryPaths';

function MemberAgreementButton({
  holding,
  documents,
}: {
  holding: CarryHoldingDetail;
  documents: CarryFundDocuments | undefined;
}) {
  const downloadMutation = useDownloadCarryDocument();
  const agreement = documents?.documents.find(
    (d) => d.id === holding.member_agreement_id && d.has_file
  );

  return (
    <CarryMemberAgreementButton
      agreementId={agreement?.id ?? null}
      disabled={downloadMutation.isPending}
      onOpen={() =>
        agreement &&
        downloadMutation.mutate({
          fundId: holding.fund_id,
          document: agreement,
        })
      }
    />
  );
}

function HoldingStats({
  holding,
  portfolio,
}: {
  holding: CarryHoldingDetail;
  portfolio: CarryMemberPortfolio | undefined;
}) {
  return (
    <CarryStatStrip
      stats={[
        {
          icon: Layers,
          label: 'Your units',
          value: formatUnits(holding.units),
          hint: `of ${formatUnits(holding.total_carry_units)}`,
        },
        {
          icon: Percent,
          label: '% of pool',
          value: formatShare(
            poolShare(holding.units, holding.total_carry_units),
            1
          ),
        },
        PAID_IN_STAT,
        ...vestingStats(holding),
        {
          icon: Gem,
          label: 'Vested value',
          value: formatCurrencyCompact(holding.vested_value, holding.currency),
          hint: `of ${formatCurrencyCompact(holding.value, holding.currency)}`,
        },
        {
          icon: Lock,
          label: 'Unvested value',
          value: formatCurrencyCompact(
            holding.unvested_value,
            holding.currency
          ),
        },
        ...(portfolio ? portfolioStats(portfolio.summary) : []),
      ]}
    />
  );
}

export function PortalCarryFundPage() {
  const { fundId = '' } = useParams<{ fundId: string }>();
  const holdingQuery = useCarryHolding(fundId);
  const portfolioQuery = useCarryHoldingPortfolio(fundId);
  const documentsQuery = useCarryFundDocuments(fundId);
  const companyCount = portfolioQuery.data?.companies.length;
  const documentCount = documentsQuery.data?.documents.length;

  return (
    <DataLoader<CarryHoldingDetail> query={holdingQuery}>
      {(holding) => (
        <div className="space-y-6">
          <div className="space-y-1">
            <PageBreadcrumb
              segments={[
                { label: 'Fund carry', href: PORTAL_CARRY_PATH },
                { label: holding.fund_name },
              ]}
              actions={
                <MemberAgreementButton
                  holding={holding}
                  documents={documentsQuery.data}
                />
              }
            />
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
              <p>{carryFundFacts(holding)}</p>
              <InformationAsOfLabel date={holding.as_of} />
            </div>
          </div>

          <HoldingStats holding={holding} portfolio={portfolioQuery.data} />
          <CarryVestingProgress holding={holding} />

          <Tabs defaultValue="portfolio">
            <TabsList>
              <TabsTrigger value="portfolio">
                Portfolio
                {companyCount !== undefined && ` (${companyCount})`}
              </TabsTrigger>
              <TabsTrigger value="documents">
                Documents
                {documentCount !== undefined && ` (${documentCount})`}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="portfolio" className="mt-6">
              <DataLoader<CarryMemberPortfolio> query={portfolioQuery}>
                {(portfolio) => (
                  <CarryPortfolioCompanies companies={portfolio.companies} />
                )}
              </DataLoader>
            </TabsContent>
            <TabsContent value="documents" className="mt-6">
              <DataLoader<CarryFundDocuments> query={documentsQuery}>
                {(documents) => <CarryFundDocumentsList fund={documents} />}
              </DataLoader>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </DataLoader>
  );
}
